import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useAxiosInstance } from "../../services/Interceptor";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

const BUTTON_STYLES = {
  desktop:
    "flex items-center gap-2 text-white bg-gray-700 font-medium px-6 py-2 rounded-full text-lg hover:bg-gray-600/70 transition",
  "mobile-menu":
    "flex items-center gap-2 text-lg text-white pt-8 hover:font-semibold",
  "mobile-dropdown": "flex items-center gap-2 text-gray-700 text-lg",
};

// How long to wait for the handshake before removing the message listener.
const AUTOLOGIN_HANDSHAKE_TIMEOUT_MS = 10000;

const HS_URL = (process.env.REACT_APP_HS_AUTOLOGIN_URL || "").replace(
  /\/$/,
  "",
);
function getHsOrigin() {
  try {
    return HS_URL ? new URL(HS_URL).origin : "";
  } catch {
    return "";
  }
}

function applySignIn(data, signIn, navigate) {
  if (!data?.accessToken || !data?.user) {
    toast.error("Invalid login token. Please try again.");
    return navigate("/sign-in");
  }
  if (data.user.role?.toLowerCase() !== "university") {
    toast.error("You do not have access to this section.");
    return navigate("/sign-in");
  }
  if (
    !signIn({
      auth: { token: data.accessToken, type: "Bearer" },
      refresh: data.refreshToken,
      expiresIn: data.expiresIn ?? 3600,
      userState: { user: data.user },
    })
  ) {
    toast.error("Error during sign-in.");
    return navigate("/sign-in");
  }
  navigate("/university/dashboard", { replace: true });
}

function HSPlatformButton({ variant }) {
  const authHeader = useAuthHeader();
  const { user } = useAuthUser() || {};
  // Cleanup for a handshake started by the current click, also run on unmount.
  const cleanupHandshakeRef = useRef(null);

  const handleClick = useCallback(() => {
    const token = authHeader?.split(" ")[1];
    if (!HS_URL || !token) return;
    const win = window.open(`${HS_URL}/auto-login`, "_blank");
    if (!win) return;
    const origin = getHsOrigin();

    // Supersede a previous click's still-pending handshake, if any.
    cleanupHandshakeRef.current?.();

    let timeoutId;
    const cleanup = () => {
      window.removeEventListener("message", listener);
      clearTimeout(timeoutId);
      cleanupHandshakeRef.current = null;
    };
    const listener = (e) => {
      if (e.origin !== origin || e.data?.type !== "HS_AUTOLOGIN_READY") return;
      win.postMessage({ type: "HS_AUTOLOGIN_TOKEN", token }, origin);
      cleanup();
    };
    window.addEventListener("message", listener);
    timeoutId = setTimeout(cleanup, AUTOLOGIN_HANDSHAKE_TIMEOUT_MS);
    cleanupHandshakeRef.current = cleanup;
  }, [authHeader]);

  // Clear a pending handshake if this button unmounts before it resolves.
  useEffect(() => {
    return () => cleanupHandshakeRef.current?.();
  }, []);

  if (
    user?.role !== "university" ||
    process.env.REACT_APP_SHOW_AUTO_LOGIN_LINK !== "yes"
  )
    return null;

  const button = (
    <button
      onClick={handleClick}
      className={BUTTON_STYLES[variant] ?? BUTTON_STYLES.desktop}
    >
      High School Platform
      <FontAwesomeIcon
        icon={faArrowUpRightFromSquare}
        className={variant === "mobile-dropdown" ? "text-xs" : "text-sm"}
      />
    </button>
  );

  if (variant === "mobile-dropdown") {
    return (
      <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer">{button}</li>
    );
  }

  return button;
}

function AutoLoginPage() {
  const apiInstance = useAxiosInstance();
  const signIn = useSignIn();
  const navigate = useNavigate();

  useEffect(() => {
    const origin = getHsOrigin();

    if (window.opener) {
      if (!origin) {
        toast.error("High School platform URL is not configured.");
        navigate("/sign-in");
        return;
      }
      let timeout;
      const handleToken = async (e) => {
        if (e.origin !== origin || e.data?.type !== "COLLEGE_AUTOLOGIN_TOKEN")
          return;
        window.removeEventListener("message", handleToken);
        clearTimeout(timeout);
        try {
          const { data } = await apiInstance.post("/user/token-login", {
            code: e.data.token,
          });
          applySignIn(data, signIn, navigate);
        } catch {
          toast.error("An error occurred during the login process.");
          navigate("/sign-in");
        }
      };
      window.addEventListener("message", handleToken);
      timeout = setTimeout(
        () => window.removeEventListener("message", handleToken),
        AUTOLOGIN_HANDSHAKE_TIMEOUT_MS,
      );
      window.opener.postMessage({ type: "COLLEGE_AUTOLOGIN_READY" }, origin);
      return () => {
        window.removeEventListener("message", handleToken);
        clearTimeout(timeout);
      };
    }

    // Cookie-based flow (acceso directo, sin opener)
    apiInstance
      .post("/user/token-login", {}, { withCredentials: true })
      .then(({ data }) => applySignIn(data, signIn, navigate))
      .catch(() => {
        toast.error("An error occurred during the login process.");
        navigate("/sign-in");
      });
  }, [apiInstance, signIn, navigate]); // eslint-disable-line

  return null;
}

export default function AutoLogin({ navButton = false, variant = "desktop" }) {
  return navButton ? <HSPlatformButton variant={variant} /> : <AutoLoginPage />;
}
